import { useState } from 'react';
import { useChain } from '@cosmos-kit/react';
import type { ProposalInfo } from '@/types/governance';
import {
  VoteOption,
  getVoteOptionLabel,
  calculateVotePercentages,
} from '@/types/governance';
import { MsgVote } from 'cosmjs-types/cosmos/gov/v1beta1/tx';
import { VoteOption as ProtoVoteOption } from 'cosmjs-types/cosmos/gov/v1beta1/gov';

interface VoteModalProps {
  proposal: ProposalInfo;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  hasDelegations?: boolean;
  totalStakedDisplay?: string;
}

export function VoteModal({
  proposal,
  isOpen,
  onClose,
  onSuccess,
  hasDelegations = true,
  totalStakedDisplay,
}: VoteModalProps): JSX.Element | null {
  const [selectedOption, setSelectedOption] = useState<VoteOption | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);

  const { address, getSigningStargateClient, isWalletConnected } = useChain(
    'gravitybridge'
  );

  const percentages = calculateVotePercentages(proposal.final_tally_result);
  const title = proposal.content?.title ?? `Proposal #${proposal.proposal_id}`;

  const voteOptions = [
    { option: VoteOption.YES, label: 'Yes', color: 'bg-green-500 hover:bg-green-600' },
    { option: VoteOption.ABSTAIN, label: 'Abstain', color: 'bg-gray-400 hover:bg-gray-500' },
    { option: VoteOption.NO, label: 'No', color: 'bg-red-500 hover:bg-red-600' },
    {
      option: VoteOption.NO_WITH_VETO,
      label: 'No with Veto',
      color: 'bg-red-800 hover:bg-red-900',
    },
  ];

  const handleVote = async (): Promise<void> => {
    if (selectedOption === null || address === undefined || address === '' || !isWalletConnected) {
      setError('Please connect your wallet and select a vote option');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setTxHash(null);

    try {
      const client = await getSigningStargateClient();

      // Map our VoteOption enum to the proto VoteOption enum (same numeric values)
      const protoOption = selectedOption as unknown as ProtoVoteOption;

      const voteMsg = {
        typeUrl: '/cosmos.gov.v1beta1.MsgVote',
        value: MsgVote.fromPartial({
          proposalId: BigInt(proposal.proposal_id),
          voter: address,
          option: protoOption,
        }),
      };

      const fee = {
        amount: [{ denom: 'ugraviton', amount: '5000' }],
        gas: '200000',
      };

      const result = await client.signAndBroadcast(address, [voteMsg], fee, `Vote on Proposal #${proposal.proposal_id}`);

      if (result.code !== 0) {
        throw new Error(`Transaction failed: ${result.rawLog}`);
      }

      setTxHash(result.transactionHash);
      onSuccess?.();
    } catch (err) {
      console.error('Vote error:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit vote');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (): void => {
    if (!isSubmitting) {
      setSelectedOption(null);
      setError(null);
      setTxHash(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            {/* Header */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Vote on Proposal #{proposal.proposal_id}
              </h3>
              <p className="text-sm text-gray-600 line-clamp-2">{title}</p>
            </div>

            {/* Current Tally */}
            <div className="mb-6">
              <div className="text-sm font-medium text-gray-700 mb-2">Current Results</div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex mb-2">
                <div
                  className="bg-green-500"
                  style={{ width: `${percentages.yes}%` }}
                />
                <div
                  className="bg-gray-400"
                  style={{ width: `${percentages.abstain}%` }}
                />
                <div
                  className="bg-red-500"
                  style={{ width: `${percentages.no}%` }}
                />
                <div
                  className="bg-red-800"
                  style={{ width: `${percentages.noWithVeto}%` }}
                />
              </div>
              <div className="grid grid-cols-4 gap-2 text-xs text-gray-600">
                <span>Yes: {percentages.yes.toFixed(1)}%</span>
                <span>Abstain: {percentages.abstain.toFixed(1)}%</span>
                <span>No: {percentages.no.toFixed(1)}%</span>
                <span>Veto: {percentages.noWithVeto.toFixed(1)}%</span>
              </div>
            </div>

            {/* Vote Options */}
            {txHash === null && hasDelegations && (
              <div className="mb-6">
                <div className="text-sm font-medium text-gray-700 mb-3">Select Your Vote</div>
                {totalStakedDisplay !== undefined && totalStakedDisplay !== '' && (
                  <p className="text-sm text-gray-600 mb-3">
                    Your voting power: <span className="font-semibold">{totalStakedDisplay} GRAV</span>
                  </p>
                )}
                <div className="grid grid-cols-2 gap-3">
                  {voteOptions.map(({ option, label, color }) => (
                    <button
                      key={option}
                      onClick={() => setSelectedOption(option)}
                      disabled={isSubmitting}
                      className={`px-4 py-3 rounded-lg font-medium text-white transition-all ${
                        selectedOption === option
                          ? `${color} ring-2 ring-offset-2 ring-blue-500`
                          : `${color} opacity-80 hover:opacity-100`
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* No Delegation Warning */}
            {txHash === null && !hasDelegations && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-yellow-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <div>
                    <div className="text-yellow-800 font-medium">Cannot Vote</div>
                    <div className="text-sm text-yellow-700">
                      You must delegate tokens to vote on governance proposals. 
                      Visit the Staking page to delegate GRAV to a validator.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Success Message */}
            {txHash !== null && txHash !== '' && selectedOption !== null && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="text-green-800 font-medium mb-1">Vote Submitted!</div>
                <div className="text-sm text-green-700">
                  Your vote for "{getVoteOptionLabel(selectedOption)}" has been submitted.
                </div>
                <a
                  href={`https://www.mintscan.io/gravity-bridge/txs/${txHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline mt-2 inline-block"
                >
                  View transaction →
                </a>
              </div>
            )}

            {/* Error Message */}
            {error !== null && error !== '' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="text-red-800 text-sm">{error}</div>
              </div>
            )}

            {/* Wallet Warning */}
            {!isWalletConnected && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="text-yellow-800 text-sm">
                  Please connect your wallet to vote on this proposal.
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-3">
            {txHash === null && hasDelegations && (
              <button
                type="button"
                onClick={() => { void handleVote(); }}
                disabled={selectedOption === null || isSubmitting || !isWalletConnected}
                className="inline-flex w-full justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:bg-gray-300 disabled:cursor-not-allowed sm:w-auto transition-colors"
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Submit Vote'
                )}
              </button>
            )}
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 sm:mt-0 sm:w-auto"
            >
              {txHash !== null && txHash !== '' ? 'Close' : 'Cancel'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
